import { CompanyUser } from '@/api';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useCompaniesTranslation } from '../hooks';
import { isCompanyOwner } from '../util/company-roles';
import { addOrUpdateCompanyUsersCache } from '../queries/company-cache';

export type CompanyRole = 'owner' | 'admin' | 'billing' | 'member';

type UseUpdateCompanyRoleActionProps = {
    user: CompanyUser;
    /** Joined owners in the company. The last one cannot be demoted. */
    ownerCount: number;
};

type UseUpdateCompanyRoleActionData = {
    /** Whether this viewer can change this member's company role right now. */
    isAllowed: boolean;
    /** Why not, ready to show in a tooltip. Empty while allowed. */
    reason: string;
    updateCompanyRole: (role: CompanyRole) => Promise<void>;
    loading: boolean;
    error: string;
};

/**
 * Changing a member's company role: whether this viewer may do it, why not when
 * they may not, and the call itself. Kept together so a caller can wire up a
 * button — disabled state, tooltip, spinner and click — from one hook.
 */
export function useUpdateCompanyRoleAction(
    props: UseUpdateCompanyRoleActionProps
): UseUpdateCompanyRoleActionData {
    const { user, ownerCount } = props;

    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    const canManageRoles = user.capabilities.canManageRoles;
    const [isAllowed, reason] = useMemo(() => {
        // The API decides who is permitted to change this member's role, and
        // hands back a ready-to-show reason when they are not.
        if (!canManageRoles.allowed) {
            return [
                false,
                canManageRoles.reason ??
                    t('actions.update_company_role.reason_not_permitted')
            ];
        }

        // Whether the change is a valid move is still ours to work out.
        if (isCompanyOwner(user) && ownerCount <= 1) {
            return [false, t('actions.update_company_role.reason_last_owner')];
        }

        return [true, ''];
    }, [canManageRoles, user, ownerCount, t]);

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error `PATCH /v3/users/{id}/roles`
    // declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'CANNOT_REMOVE_LAST_OWNER')) {
                return t('actions.update_company_role.error_last_owner');
            }
            if (isApiError(error, 'CANNOT_ASSIGN_OWNER_ROLE')) {
                return t(
                    'actions.update_company_role.error_cannot_assign_owner'
                );
            }
            if (isApiError(error, 'COMPANY_USER_NOT_FOUND')) {
                return t('actions.update_company_role.error_not_found');
            }
            if (isApiError(error, 'FORBIDDEN')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: (role: CompanyRole) =>
            openApiRef.current.users
                .updateUserRoles(user.id, { role })
                .then((response) => response.data.data.user),
        onSuccess: (updated) => {
            addOrUpdateCompanyUsersCache(queryClient, updated);

            UIToast.success(t('actions.update_company_role.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        isAllowed,
        reason,
        updateCompanyRole: (role: CompanyRole) =>
            mutation.mutateAsync(role).then(() => {}),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
